import axios from 'axios';
import { CreateBookingDto, Booking, Availability, MarkOnTheWayDto, OnTheWayResponse, BarberMyDay } from '../types';
import { API_BASE_URL } from '../config/api';

// Next Available Slot Response
export interface NextAvailableSlot {
  slotDateTime: string;
  date: string;
  timeLabel: string;
  dayOfWeek: string;
}

// Enhanced booking update request
interface UpdateEnhancedBookingDto {
  bookingDateTime?: string;
  serviceIds?: number[];
  notes?: string;
}

// Cancel booking request
interface CancelBookingDto {
  bookingId?: number;
  reason?: string;
  notifyClient?: boolean;
  allowRefund?: boolean;
  cancellationReason?: string;
}

// Next available slot query params
interface NextAvailableSlotParams {
  barberId: number;
  barbershopId: number;
  serviceDurationMinutes: number;
  startDate?: string;
}

const bookingApi = axios.create({
  baseURL: `${API_BASE_URL}/api/v1/bookings`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add authentication token to requests
bookingApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const bookingsApi = {
  // Create a new booking
  createBooking: async (bookingData: CreateBookingDto): Promise<Booking> => {
    const response = await bookingApi.post('/', bookingData);
    return response.data;
  },

  // Get user's bookings
  getUserBookings: async (): Promise<Booking[]> => {
    const response = await bookingApi.get('/');
    // API returns PaginatedResponse<BookingDto>, extract the items array
    const data = response.data;
    if (data && Array.isArray(data.items)) {
      return data.items;
    }
    // Fallback for backward compatibility if API returns array directly
    if (Array.isArray(data)) {
      return data;
    }
    return [];
  },

  // Get specific booking
  getBooking: async (bookingId: number): Promise<Booking> => {
    const response = await bookingApi.get(`/${bookingId}`);
    return response.data;
  },

  // Update booking status
  updateBookingStatus: async (bookingId: number, status: string): Promise<Booking> => {
    const response = await bookingApi.patch(`/${bookingId}/status`, { Status: status });
    return response.data;
  },

  // Cancel booking
  cancelBooking: async (bookingId: number, cancellationReason?: string): Promise<void> => {
    await bookingApi.post(`/${bookingId}/cancel`, { 
      id: bookingId,
      cancellationReason: cancellationReason || 'User requested cancellation' 
    });
  },

  // Get availability for a barber
  getAvailability: async (
    barbershopId: number, 
    barberId: number, 
    date: string
  ): Promise<Availability[]> => {
    const response = await bookingApi.get('/availability', {
      params: { barbershopId, barberId, date }
    });
    return response.data;
  },

  // Check booking eligibility
  checkEligibility: async (): Promise<{ isEligible: boolean }> => {
    const response = await bookingApi.get('/eligibility');
    return response.data;
  },

  // Enhanced booking endpoints
  updateEnhancedBooking: async (bookingId: number, updateData: UpdateEnhancedBookingDto): Promise<Booking> => {
    const enhancedBookingApi = axios.create({
      baseURL: `${API_BASE_URL}/api/v1/enhancedbooking`,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
      },
    });

    const response = await enhancedBookingApi.put(`/${bookingId}`, updateData);
    return response.data;
  },

  cancelEnhancedBooking: async (bookingId: number, cancelData: CancelBookingDto): Promise<void> => {
    const enhancedBookingApi = axios.create({
      baseURL: `${API_BASE_URL}/api/v1/enhancedbooking`,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
      },
    });

    await enhancedBookingApi.delete(`/${bookingId}/cancel`, { data: cancelData });
  },

  // Find next available appointment slot
  findNextAvailableSlot: async (
    barberId: number,
    barbershopId: number,
    serviceDurationMinutes: number,
    startDate?: string
  ): Promise<NextAvailableSlot> => {
    const params: NextAvailableSlotParams = {
      barberId,
      barbershopId,
      serviceDurationMinutes
    };

    if (startDate) {
      params.startDate = startDate;
    }

    const response = await bookingApi.get('/next-available-slot', { params });
    return response.data;
  },

  // Update booking notes
  updateBookingNotes: async (
    bookingId: number,
    notes: string,
    bookingDateTime: string,
    durationMinutes: number = 30
  ): Promise<Booking> => {
    const response = await bookingApi.put(`/${bookingId}`, {
      id: bookingId,
      bookingDateTime,
      durationMinutes,
      notes,
    });
    return response.data;
  },

  // Mark booking as "On The Way" - customer notifies barber they're heading over
  markOnTheWay: async (
    bookingId: number,
    dto?: MarkOnTheWayDto
  ): Promise<OnTheWayResponse> => {
    const response = await bookingApi.post(`/${bookingId}/on-the-way`, dto || {});
    return response.data;
  },

  // Get barber's "My Day" view with today's appointments organized by status
  getBarberMyDay: async (
    barberId: number,
    date?: string
  ): Promise<BarberMyDay> => {
    // Add cache-busting timestamp to ensure fresh data
    const params: Record<string, string> = { _t: Date.now().toString() };
    if (date) {
      params.date = date;
    }
    const response = await bookingApi.get(`/my-day/${barberId}`, { params });
    return response.data;
  },

  // Barber checks in a customer (marks them as arrived and waiting)
  barberCheckIn: async (bookingId: number): Promise<void> => {
    await bookingApi.post(`/${bookingId}/barber-check-in`);
  },
};